import { useState } from 'react';

import { ChevronDownIcon, ChevronUpDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { AdjustmentsHorizontalIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { SectionTitle } from '@/components/common/SectionTitle';
import { policyName } from '@/features/decisions/config/personaContext';
import type { FcfEntitlementBar } from '@/features/decisions/decisions.api.types';
import { fmtMoneyM, fmtMoneyMWithSign, fmtPointsWithSign } from '@/features/decisions/utils/fcfFormat';

interface Props {
  refBar: FcfEntitlementBar | undefined;
  cmpBars: FcfEntitlementBar[];
}

type Mode = 'absolute' | 'relative';
type MetricKey = 'avgInv' | 'startInv' | 'endInv' | 'fillRate' | 'prodCost' | 'netRevenue' | 'cashFlow';

interface ModelRow {
  experiment_id: string;
  name: string;
  isReference: boolean;
  rank: number;
  metrics: Record<MetricKey, number | null>;
}

// Column order mirrors the wireframe. `metric` columns are sortable + API-backed;
// `placeholder` columns have no wired source yet and render "—".
const COLUMNS: Array<
  | { id: 'rank'; label: string; type: 'rank' }
  | { id: 'name'; label: string; type: 'name' }
  | { id: string; label: string; type: 'placeholder' }
  | { id: MetricKey; label: string; type: 'metric'; kind: 'money' | 'pct' }
> = [
  { id: 'rank', label: 'Rank', type: 'rank' },
  { id: 'name', label: 'Model Name', type: 'name' },
  { id: 'origination', label: 'Model Origination Date', type: 'placeholder' },
  { id: 'avgInv', label: 'Average Inventory', type: 'metric', kind: 'money' },
  { id: 'startInv', label: 'Starting Inventory', type: 'metric', kind: 'money' },
  { id: 'endInv', label: 'Ending Inventory', type: 'metric', kind: 'money' },
  { id: 'fillRate', label: 'Average Fill Rate (Revenue)', type: 'metric', kind: 'pct' },
  { id: 'prodCost', label: 'Production Costs', type: 'metric', kind: 'money' },
  { id: 'netRevenue', label: 'Sum Net Revenue', type: 'metric', kind: 'money' },
  { id: 'cashFlow', label: 'Cash Flow', type: 'metric', kind: 'money' },
];

const sum = (a: number | null | undefined, b: number | null | undefined): number | null =>
  a == null && b == null ? null : (a ?? 0) + (b ?? 0);

function toMetrics(b: FcfEntitlementBar): Record<MetricKey, number | null> {
  return {
    avgInv: b.model_ending_onhand_value,
    startInv: b.model_starting_onhand_value ?? null,
    endInv: b.model_final_onhand_value ?? null,
    fillRate: b.available_in_full_per_period,
    prodCost: b.model_order_placed_total_cost,
    netRevenue: sum(b.model_revenue_demand_fulfilled, b.model_revenue_backlog_fulfilled),
    cashFlow: b.model_fcf,
  };
}

export function ResultMetricsTable({ refBar, cmpBars }: Props) {
  const [mode, setMode] = useState<Mode>('absolute');
  const [sortKey, setSortKey] = useState<MetricKey>('cashFlow');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const bars = refBar ? [refBar, ...cmpBars] : cmpBars;
  if (bars.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white px-4 py-6 text-sm text-gray-500">
        No result metrics for this scenario.
      </div>
    );
  }

  const ref = refBar ? toMetrics(refBar) : null;

  // Rank by Cash Flow desc — the objective — independent of the display sort.
  const rankOrder = [...bars].sort((a, b) => (b.model_fcf ?? -Infinity) - (a.model_fcf ?? -Infinity));
  const rankById = new Map(rankOrder.map((b, i) => [b.experiment_id, i + 1]));

  const rows: ModelRow[] = bars.map((b) => ({
    experiment_id: b.experiment_id,
    name: policyName(b.experiment_id, b.is_reference),
    isReference: b.is_reference,
    rank: rankById.get(b.experiment_id) ?? 0,
    metrics: toMetrics(b),
  }));

  const sorted = [...rows].sort((a, b) => {
    const av = a.metrics[sortKey];
    const bv = b.metrics[sortKey];
    if (av == null) return 1;
    if (bv == null) return -1;
    return sortDir === 'desc' ? bv - av : av - bv;
  });

  function onSort(key: MetricKey) {
    if (key === sortKey) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  function cell(row: ModelRow, key: MetricKey, kind: 'money' | 'pct'): string {
    const v = row.metrics[key];
    if (v == null) return '—';
    if (mode === 'absolute') return kind === 'money' ? fmtMoneyM(v) : `${(v * 100).toFixed(1)}%`;
    if (row.isReference || ref == null || ref[key] == null) return '—'; // incumbent = baseline
    const d = v - (ref[key] as number);
    return kind === 'money' ? fmtMoneyMWithSign(d) : fmtPointsWithSign(d);
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="flex items-center justify-between px-4 py-4">
        <SectionTitle>Result Metrics</SectionTitle>
        <div className="flex items-center gap-3 text-gray-400">
          <button type="button" aria-label="Filter" className="hover:text-gray-600">
            <AdjustmentsHorizontalIcon className="h-4 w-4" />
          </button>
          <button type="button" aria-label="Download" className="hover:text-gray-600">
            <ArrowDownTrayIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Absolute / Relative toggle */}
      <div className="px-4 pb-3">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm">
          {(['absolute', 'relative'] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={`rounded-md px-3 py-1 font-medium capitalize transition-colors ${
                mode === m ? 'bg-white text-[#274483] shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border-t border-gray-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 text-left align-bottom text-xs font-medium text-gray-500">
              {COLUMNS.map((c) => {
                const sortable = c.type === 'metric';
                const active = sortable && c.id === sortKey;
                return (
                  <th key={c.id} className={`px-4 py-2 ${c.type === 'metric' ? 'text-right' : ''}`}>
                    {sortable ? (
                      <button
                        type="button"
                        onClick={() => onSort(c.id as MetricKey)}
                        className="inline-flex items-center gap-1 hover:text-gray-700"
                      >
                        <span>{c.label}</span>
                        {active ? (
                          sortDir === 'desc' ? (
                            <ChevronDownIcon className="h-3.5 w-3.5 text-[#274483]" />
                          ) : (
                            <ChevronUpIcon className="h-3.5 w-3.5 text-[#274483]" />
                          )
                        ) : (
                          <ChevronUpDownIcon className="h-3.5 w-3.5 text-gray-300" />
                        )}
                      </button>
                    ) : (
                      c.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map((row) => {
              const winner = row.rank === 1;
              return (
                <tr key={row.experiment_id} className={winner ? 'bg-emerald-50/60' : 'hover:bg-gray-50'}>
                  {COLUMNS.map((c) => {
                    if (c.type === 'rank') {
                      return (
                        <td key={c.id} className="px-4 py-3 text-gray-500 tabular-nums">
                          {row.rank}
                        </td>
                      );
                    }
                    if (c.type === 'name') {
                      return (
                        <td key={c.id} className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#274483]">{row.name}</span>
                            {/* {winner && (
                              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                                Winner
                              </span>
                            )} */}
                          </div>
                        </td>
                      );
                    }
                    if (c.type === 'placeholder') {
                      return (
                        <td key={c.id} className="px-4 py-3 text-gray-400">
                          —
                        </td>
                      );
                    }
                    const isCash = c.id === 'cashFlow';
                    return (
                      <td
                        key={c.id}
                        className={`px-4 py-3 text-right tabular-nums ${isCash ? 'font-semibold text-emerald-600' : 'text-gray-900'}`}
                      >
                        {cell(row, c.id, c.kind)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-500">
        Showing {rows.length} of {rows.length} models · ranked by Cash Flow
      </div>
    </div>
  );
}
